import FF from "@/components/forms/FormField.module.css";

import { FormField } from "@/components/forms/FormField.tsx";
import { CurrencyHourly, EstimatedCurrencyHourly } from "@/components/l10n/Currency.tsx";
import { FullPriceWithDiscount } from "@/components/PriceWithDiscount.tsx";
import { PricingModal, type PricingModalDataProps } from "@/modules/pricing/PricingModal.tsx";
import type { Price } from "@/modules/pricing/resourcePricing.ts";
import type { BaseProps } from "@/utils/baseProps.ts";
import { cn } from "@/utils/classNames.ts";
import { Trans } from "@lingui/react/macro";
import { notNull } from "@warrenio/utils/notNull";
import type { ReactNode } from "react";

interface MonthlyCostElementProps extends BaseProps {
    price: Price;
    emphasize?: boolean;
    inline?: boolean;
    compact?: boolean;
    title?: string;
    estimated?: boolean;
    billingAccountId?: number;
}

function Unit({ value }: { value: "GB" | undefined }) {
    return value ? <Trans>GB</Trans> : null;
}

function Period({ value }: { value: "month" | "hour" }) {
    return value === "month" ? <Trans>month</Trans> : <Trans>hour</Trans>;
}

const denominator = (period: "month" | "hour", price: Price) => (
    <>
        {price.unit && (
            <>
                / <Unit value={price.unit} />{" "}
            </>
        )}
        / <Period value={period} />
    </>
);

const monthlyElement = (price: Price, billingAccountId?: number, clsName?: string) => (
    <>
        <span className={clsName} data-testid="priceValue">
            <FullPriceWithDiscount price={price} billingAccountId={billingAccountId} allowFree={false} />
        </span>{" "}
        <span className="text-nowrap">{denominator("month", price)}</span>
    </>
);

// eslint-disable-next-line react-refresh/only-export-components
export const Free = <Trans comment="At no cost. | POS: Noun">Free</Trans>;

export function MonthlyCostElement({
    price,
    emphasize = true,
    inline,
    compact,
    title,
    estimated,
    billingAccountId,
    ...divProps
}: MonthlyCostElementProps) {
    const fontSize = emphasize ? "font-size-title" : "";

    const hourlyElement = (
        <>
            <span data-testid="priceValue">
                {estimated ? (
                    <FullPriceWithDiscount
                        price={price}
                        billingAccountId={billingAccountId}
                        allowFree={false}
                        renderPrice={({ discountedHourlyAmount }) => (
                            <EstimatedCurrencyHourly value={discountedHourlyAmount} compact={compact} />
                        )}
                    />
                ) : (
                    <FullPriceWithDiscount
                        price={price}
                        billingAccountId={billingAccountId}
                        allowFree={false}
                        renderPrice={({ discountedHourlyAmount, price }) =>
                            discountedHourlyAmount !== price.hourly ? (
                                <>
                                    {!!discountedHourlyAmount && (
                                        <span className="color-success inline-block pr-1">
                                            <CurrencyHourly value={discountedHourlyAmount} compact={compact} />
                                        </span>
                                    )}
                                    <span className="line-through">
                                        <CurrencyHourly value={price.hourly} compact={compact} />
                                    </span>
                                </>
                            ) : (
                                <CurrencyHourly value={discountedHourlyAmount} compact={compact} />
                            )
                        }
                    />
                )}
            </span>{" "}
            <span className="text-nowrap">{denominator("hour", price)}</span>
        </>
    );

    return (
        <div {...divProps}>
            {title && (
                <div className={cn(FF.FormFieldMessage, "color-muted", compact && "font-size-small")}>{title}</div>
            )}
            {price.hourly === 0 ? (
                <div className={fontSize}>{inline ? <b>{Free}</b> : Free}</div>
            ) : inline ? (
                <>
                    <b>{monthlyElement(price, billingAccountId, fontSize)}</b> ({hourlyElement})
                </>
            ) : (
                <>
                    <div>
                        {monthlyElement(price, billingAccountId, fontSize)}
                        <span className="font-size-title" />
                    </div>
                    <div>{hourlyElement}</div>
                </>
            )}
        </div>
    );
}

interface SimpleMonthlyCostElementProps extends BaseProps {
    price: Price;
    show_zero_as: "FREE" | "NUMBER";
}

export function SimpleMonthlyCostElement({ price, show_zero_as = "FREE", ...divProps }: SimpleMonthlyCostElementProps) {
    const showFreeText = show_zero_as === "FREE" && price.hourly === 0;
    return <div {...divProps}>{showFreeText ? Free : monthlyElement(price)}</div>;
}

interface MonthlyCostFieldProps extends PricingModalDataProps {
    priceDescription?: ReactNode;
    description?: ReactNode;
    price?: Price;
    billingAccountId?: number;

    children?: ReactNode;
}

export function MonthlyCostField({
    description,
    priceDescription,
    children,
    price,
    billingAccountId,
    ...props
}: MonthlyCostFieldProps) {
    priceDescription ??= (
        <Trans>
            The price is approximate, the cost of your resource will be calculated based on your actual hourly usage.
        </Trans>
    );

    description ??= (
        <>
            {priceDescription}{" "}
            <PricingModal {...props}>
                <Trans>Please see how the cost of your resource is calculated.</Trans>
            </PricingModal>
        </>
    );

    children ??= (
        <MonthlyCostElement
            price={notNull(price, "Must pass price when no children")}
            billingAccountId={billingAccountId}
        />
    );

    return (
        <FormField label={<Trans>Estimated cost</Trans>} description={description}>
            {/* XXX: className={FF.FormFieldHolder} this seems to be not needed */}
            <div data-testid="monthlyCost">{children}</div>
        </FormField>
    );
}
